import { useAuth } from '@contexts/auth-provider';
import { FeedbackContent } from '@shared/components/feedback-content/FeedbackContent';
import { FEEDBACK_CONFIGS } from '@shared/components/feedback-content/FeedbackContent.utils';
import { navigateToHelp } from '@shared/helpers/navigate-to-help/navigateToHelp';
import { useLogout } from '@shared/hooks/use-logout/useLogout';
import * as React from 'react';

import type { ErrorPageProps } from './ErrorPage.types';

import styles from './ErrorPage.module.css';
import { getLogoutModeActions } from './ErrorPage.utils';

/**
 * Full-page error component for use OUTSIDE layouts (no header/nav visible).
 *
 * Always shows logout + help actions so the user can escape.
 * - External users: "Get help" (primary) + "Log out" (secondary)
 * - Internal users: "Log out" (primary only)
 *
 * For errors INSIDE layouts (where the header provides logout/help),
 * use FeedbackContent directly with "Go to Homepage" action instead.
 *
 * Used by:
 * - Route error pages (public.routes.tsx catch-all)
 * - AppInitializer (fatal init errors)
 * - AppLayout (permissions load failure)
 * - OrganizationRouteGuard (org fetch error)
 * - AppContent (global 403 intercept via forbiddenStore)
 * - ModulesProvider / OrganizationsProvider (GET or fatal selection errors)
 * - PermissionsProvider (GET fetch error)
 */
const ErrorPage = React.memo(
  ({ type }: Readonly<ErrorPageProps>): React.ReactElement => {
    const { authData } = useAuth();
    const logout = useLogout();
    const config = FEEDBACK_CONFIGS[type];
    const isInternalUser = authData?.isInternalUser ?? false;

    const {
      primaryLabel,
      primaryAction,
      showPrimary,
      secondaryLabel,
      secondaryAction,
    } = getLogoutModeActions(isInternalUser, navigateToHelp, logout);

    return (
      <div className={styles.errorPage} data-testid="error-page">
        <FeedbackContent
          title={config.title}
          titleSize="heading-02"
          message={config.message}
          illustration={<config.Illustration />}
          primaryActionLabel={primaryLabel}
          onPrimaryAction={primaryAction}
          showPrimaryAction={showPrimary}
          secondaryActionLabel={secondaryLabel}
          onSecondaryAction={secondaryAction}
        />
      </div>
    );
  }
);

ErrorPage.displayName = 'ErrorPage';

export { ErrorPage };
